/* eslint-disable  @typescript-eslint/no-explicit-any */
import '@babel/polyfill'

import envVariable from '../../presentation/util/envVariable'
import Authentication from '../../oauth/Authentication'
import axios from 'axios'

export default class Caller {

  static initApiUrl(): string {
    return `${envVariable('REACT_APP_API_URL')}/${envVariable('REACT_APP_LOCALE')}`
  }

  private static getHeaders(
    contentType = 'application/json',
    accept: string | null = 'application/json'
  ): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': contentType,
      'Authorization': `${Authentication.getToken()}`,
    }

    if (accept) {
      headers['Accept'] = accept
    }

    const role = localStorage.getItem('role')
    if (role) {
      headers['X-Esous-Role'] = role
    }

    const accountId = localStorage.getItem('accountId')
    if (accountId) {
      headers['X-Esous-Account-Id'] = accountId
    }

    return headers
  }

  private static handleForbidden(error: any): void {
    if (error?.response?.status === 403 && typeof window !== 'undefined') {
      if (window.location.pathname !== '/tableau-de-bord') {
        window.location.replace('/tableau-de-bord')
      }
    }
  }

  static async executeGet(
    path: string,
    params: Record<string, string>,
    signal?: AbortSignal
  ): Promise<any> {
    let paramsString = ''

    if (Object.keys(params).length > 0) {
      paramsString = Object.keys(params).map(key => key + '=' + params[key]).join('&')
      paramsString = `?${paramsString}`
    }

    return axios.get(`${path}${paramsString}`, {
      method: 'GET',
      headers: Caller.getHeaders(),
      signal
    })
      .then(response => {
        return response.data
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response?.status === 401) {
          return Authentication.refreshToken().then(success => success ? this.executeGet(path, params, signal) : null)
        }
        Caller.handleForbidden(error)
        return Promise.reject(error)
      })
  }

  static async executeGetResponseAsBlob(
    path: string,
    params: Record<string, string>,
    signal?: AbortSignal
  ): Promise<any> {
    let paramsString = ''

    if (Object.keys(params).length > 0) {
      paramsString = Object.keys(params).map(key => key + '=' + params[key]).join('&')
      paramsString = `?${paramsString}`
    }

    return axios.get(`${path}${paramsString}`, {
      method: 'GET',
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/pdf, application/octet-stream, application/json',
        'Authorization': `${Authentication.getToken()}`
      },
      signal
    })
      .then(response => response.data)
      .catch(async (error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response) {
          const contentType = error.response.headers['content-type']
          if (contentType && contentType.includes('application/json')) {
            const errorBlob = error.response.data
            const errorText = await errorBlob.text()
            const errorJson = JSON.parse(errorText)
            return Promise.reject(errorJson)
          }

          if (401 == error.response?.status) {
            const success = await Authentication.refreshToken()
            if (success) {
              return this.executeGetResponseAsBlob(path, params, signal)
            }
          }
        }
        Caller.handleForbidden(error)

        return Promise.reject(error)
      })
  }

  static async executePost(
    path: string,
    params: unknown,
    signal?: AbortSignal
  ): Promise<any> {
    return axios(path, {
      method: 'POST',
      headers: Caller.getHeaders(),
      data: JSON.stringify(params)
    })
      .then(response => response.data)
      .catch((error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response?.status === 401) {
          return Authentication.refreshToken().then(success => success ? this.executePost(path, params, signal) : null)
        }
        Caller.handleForbidden(error)
        return Promise.reject(error)
      })
  }

  static async executePostBinaryFile(
    path: string,
    params: unknown,
    signal?: AbortSignal
  ): Promise<any> {
    return axios.request({
      url: path,
      responseType: 'blob',
      method: 'POST',
      headers: Caller.getHeaders('application/json', null),
      data: JSON.stringify(params)
    })
      .then(response => response)
      .catch((error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response?.status === 401) {
          return Authentication.refreshToken().then(success => success ? this.executePostBinaryFile(path, params, signal) : null)
        }
        Caller.handleForbidden(error)
        return Promise.reject(error)
      })
  }

  static async executePut(
    path: string,
    params: unknown,
    signal?: AbortSignal
  ): Promise<any> {
    return axios(path, {
      method: 'PUT',
      headers: Caller.getHeaders('application/json;charset=UTF-8'),
      data: JSON.stringify(params),
      signal
    })
      .then(response => response.data)
      .catch((error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response?.status === 401) {
          return Authentication.refreshToken().then(success => success ? this.executePut(path, params, signal) : null)
        }
        Caller.handleForbidden(error)
        return Promise.reject(error)
      })
  }

  static async executeDelete(
    path: string,
    params: unknown,
    signal?: AbortSignal
  ): Promise<any> {
    return axios(path, {
      method: 'DELETE',
      headers: Caller.getHeaders('application/json;charset=UTF-8'),
      data: params,
      signal
    })
      .then(response => response.status === 204 ? true : response.data)
      .catch((error) => {
        if (error.name === 'AbortError') {
          return null
        }
        if (error.response?.status === 401) {
          return Authentication.refreshToken().then(success => success ? this.executeDelete(path, params, signal) : null)
        }
        Caller.handleForbidden(error)
        return Promise.reject(error)
      })
  }
}
